import { ChangeEvent, JSX, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Tooltip, Typography } from '@mui/material';

interface CreateBarProps {
    creating: boolean;
    onCreate: (name: string, targetUrl: string, quickScan: boolean) => void;
}

/**
 * Starting a scan is two fields and a choice, so it is one row rather than the
 * third of the screen it used to take. The two scans differ only in how much
 * they crawl, so they are one primary button and one outlined one, and the
 * difference is said once underneath instead of twice in saturated green and
 * orange under each button.
 *
 * Validation is shown on the field that is wrong. It was four `notify` toasts
 * before, which auto hide after about four seconds and never point at anything.
 */
export const EmailFinderCreateBar: (props: CreateBarProps) => JSX.Element =
    (props: CreateBarProps): JSX.Element => {
        const [name, setName] = useState<string>('');
        const [targetUrl, setTargetUrl] = useState<string>('');
        const [nameError, setNameError] = useState<string>('');
        const [urlError, setUrlError] = useState<string>('');

        function validate(): boolean {
            let ok: boolean = true;

            if (name.trim() === '') {
                setNameError('Give the scan a name');
                ok = false;
            } else if (name.length > 100) {
                setNameError('Too long, keep it under 100 characters');
                ok = false;
            } else {
                setNameError('');
            }

            const urls: string[] = targetUrl.split(',').map((part: string): string => part.trim());
            if (targetUrl.trim() === '') {
                setUrlError('Give the scan a target');
                ok = false;
            } else if (urls.some((url: string): boolean => !url.startsWith('https://'))) {
                setUrlError('Every URL must start with https://');
                ok = false;
            } else {
                setUrlError('');
            }

            return ok;
        }

        function handleSubmit(quickScan: boolean): void {
            if (!validate()) {
                return;
            }
            props.onCreate(name.trim(), targetUrl.trim(), quickScan);
            setName('');
            setTargetUrl('');
        }

        return (
            <Box sx={ {
                width: '100%',
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                paddingBottom: '18px',
                marginBottom: '18px',
            } }>
                <Typography sx={ {
                    fontSize: 'var(--wf-eyebrow)',
                    letterSpacing: 'var(--wf-track)',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontWeight: 700,
                    marginBottom: '10px',
                } }>
                    { 'New scan' }
                </Typography>

                <Box sx={ {
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: '12px',
                } }>
                    <TextField value={ name }
                               size="small"
                               label="Name"
                               placeholder="What is this scan for?"
                               error={ nameError !== '' }
                               helperText={ nameError }
                               disabled={ props.creating }
                               onChange={ (e: ChangeEvent<HTMLInputElement>): void => setName(e.target.value) }
                               sx={ { flex: '1 1 220px', minWidth: '180px' } } />

                    <TextField value={ targetUrl }
                               size="small"
                               label="Target URL"
                               placeholder="https://example.com/about-us/our-team/"
                               error={ urlError !== '' }
                               helperText={ urlError }
                               disabled={ props.creating }
                               onChange={ (e: ChangeEvent<HTMLInputElement>): void => setTargetUrl(e.target.value) }
                               sx={ { flex: '2 1 340px', minWidth: '240px' } } />

                    <Box sx={ { display: 'flex', gap: '8px', paddingTop: '1px' } }>
                        <Tooltip title="Crawls the target and every page it can reach from it">
                            <span>
                                <Button variant="contained"
                                        disableElevation
                                        disabled={ props.creating }
                                        onClick={ (): void => handleSubmit(false) }
                                        startIcon={ props.creating
                                            ? <CircularProgress size={ 15 } color="inherit" />
                                            : undefined }
                                        sx={ { height: '40px', paddingX: '18px', whiteSpace: 'nowrap' } }>
                                    { 'Full scan' }
                                </Button>
                            </span>
                        </Tooltip>

                        <Tooltip title="Reads only the URLs you list, and takes emails without following links">
                            <span>
                                <Button variant="outlined"
                                        disabled={ props.creating }
                                        onClick={ (): void => handleSubmit(true) }
                                        sx={ { height: '40px', paddingX: '18px', whiteSpace: 'nowrap' } }>
                                    { 'Quick scan' }
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                <Typography sx={ {
                    fontSize: '12px',
                    color: 'text.secondary',
                    marginTop: '8px',
                } }>
                    { 'A full scan follows every link it finds. A quick scan reads only the comma '
                        + 'separated URLs in the target field, and collects emails without crawling.' }
                </Typography>
            </Box>
        );
    }
